import { useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { zodResolver } from '@hookform/resolvers/zod'
import { ZodObject, ZodRawShape } from 'zod'
import { Button, ButtonVariant, Label, Skeleton } from '@pay-com/components'
import { CheckArrowFilled } from '@pay-com/icons'
import {
  Task,
  TaskActionButton,
  TaskField,
  TaskFieldType,
  TaskStatus,
  useSubmitTaskMutation,
  useTaskDetailsQuery
} from 'generated/graphql'
import { Individuals } from 'pages/Onboarding/types'

import { TaskAttachmentsFieldPreview } from '../TaskAttachmentsFieldPreview'
import { TaskGenericInput } from '../TaskGenericInput'
import { TaskIndividualHeader } from '../TaskIndividualHeader'
import { TaskRequestReason } from '../TaskRequestReason'
import {
  getTaskDefaultValues,
  getTaskSubmitData,
  getValidationSchema
} from './functions'

type TaskItemProps = {
  task: Task
  individuals?: Individuals
  isSubmitted: boolean
  onSubmitted: (taskId: string) => void
}

export const TaskItem = ({
  task,
  individuals,
  isSubmitted,
  onSubmitted
}: TaskItemProps) => {
  const { t } = useTranslation()

  const { data: taskDetailsData, loading } = useTaskDetailsQuery({
    fetchPolicy: 'no-cache',
    variables: {
      input: {
        id: task.id
      }
    }
  })

  const taskDetails = taskDetailsData?.task
  const fields = taskDetails?.fields ?? []
  const defaultValues = useMemo(
    () => getTaskDefaultValues(taskDetails?.values ?? []),
    [taskDetails?.values]
  )

  const newValueField = fields.find(field => field.id === 'newValue')
  const { externalId } = newValueField || {}
  const isIndividual = externalId?.includes('individuals')
  const individualId = isIndividual ? externalId?.split('.')[1] : null
  const individual = individuals?.find(
    ({ individual: i }) => i.id === individualId
  )?.individual

  const [shouldCallValidation, setShouldCallValidation] = useState(false)
  const [activeSchema, setActiveSchema] =
    useState<ZodObject<ZodRawShape> | null>(null)

  const methods = useForm({
    defaultValues,
    ...(activeSchema ? { resolver: zodResolver(activeSchema) } : {})
  })

  const [submitTask, { loading: submitLoading }] = useSubmitTaskMutation()

  useEffect(() => {
    if (!shouldCallValidation || !activeSchema) {
      return
    }

    methods.handleSubmit(() => ({}))()
    setShouldCallValidation(false)
  }, [activeSchema, shouldCallValidation])

  const handleSubmitTask = (action: TaskActionButton) => async () => {
    if (!action.signal || !fields.length) return

    const schema = getValidationSchema(fields, action.validationConfigs)
    const submitValues = methods.getValues()
    const result = schema ? schema.safeParse(submitValues) : { success: true }

    if (!result.success) {
      setActiveSchema(schema)
      setShouldCallValidation(true)
      return
    }

    const formData = Object.entries(submitValues).reduce(
      (acc, [key, value]) => {
        if (fields.find(field => field.id === key)) {
          acc[key] = value
        }
        return acc
      },
      Object.create(null) as Record<string, unknown>
    )

    await submitTask({
      variables: {
        input: {
          id: task.id,
          signal: action.signal,
          label: action.label,
          values: getTaskSubmitData(formData, fields)
        }
      }
    })

    onSubmitted(task.id)
  }

  const isReadonly = taskDetails?.status !== TaskStatus.New
  const isCompleted = taskDetails?.status === TaskStatus.Completed

  const submittedFields = fields.map((field: TaskField) => (
    <div key={field.id}>
      <Label label={field.label} />
      <div className="mt-2 flex flex-row items-center gap-2 rounded bg-gray-50 p-3">
        <CheckArrowFilled className="h-5 text-green-600" fill="currentColor" />
        <div className="text-sm text-gray-700">
          {field.type === TaskFieldType.Documents
            ? t('tasks.documents.uploaded')
            : t('tasks.informationSubmitted')}
        </div>
      </div>
    </div>
  ))

  return (
    <FormProvider {...methods}>
      <form onSubmit={e => e.preventDefault()}>
        <div className="grid items-center gap-0">
          <div className="mt-5">
            {(!isReadonly || isCompleted) && (
              <div className="grid gap-3">
                {loading ? (
                  <Skeleton className="h-[3.625rem] w-full" />
                ) : (
                  <>
                    {individual && (
                      <TaskIndividualHeader individual={individual} />
                    )}
                    {isSubmitted ? (
                      submittedFields
                    ) : (
                      <>
                        <TaskRequestReason taskDetails={taskDetails} />
                        {!!taskDetails?.attachments?.length && (
                          <TaskAttachmentsFieldPreview
                            ids={[...taskDetails.attachments]}
                            fieldLabel={t('tasks.attachments')}
                          />
                        )}
                        {fields.map((field: TaskField) => (
                          <TaskGenericInput
                            key={field.id}
                            field={field}
                            taskDetails={taskDetails}
                            isReadonly={isReadonly}
                          />
                        ))}
                      </>
                    )}
                  </>
                )}
              </div>
            )}
            <div className="mt-3 flex items-baseline justify-between">
              {!isReadonly && !isSubmitted && (
                <div className="flex grow justify-end gap-3">
                  {loading ? (
                    <Skeleton className="h-12 w-full" />
                  ) : (
                    !isCompleted &&
                    taskDetails?.actions
                      .filter(action => !!action.signal)
                      .map(action => (
                        <Button
                          key={`${action.signal}_${task.status}_${loading}`}
                          type="button"
                          variant={action.style as ButtonVariant}
                          onClick={handleSubmitTask(action)}
                          className="w-full"
                          disabled={submitLoading || loading}
                          isLoading={submitLoading}
                        >
                          {action.label}
                        </Button>
                      ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  )
}
